import { biCardSubtitle } from '@euler/app/components/message/widgets/bi-visualization/functions/biCardSubtitle';
import { biVizIcon } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizIcon';
import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDateFilterUse } from '@euler/model/bi/dashboard';
import { BiPalette, BiTitleAlign } from '@euler/model/bi/viz';
import { CalendarX } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiSurfaceCard } from '../components/ui/BiSurfaceCard';
import { BiTag } from '../components/ui/BiTag';
import { CardBody } from '../dashboard/card/CardBody';
import { biCardKind } from '../functions/biCardKind';
import { BiCardShown } from './functions/biCardShown';

/** The card itself, large: title, caption, then its chart, table or KPI. */
export const CardChartSection = memo(
  ({
    shown,
    palette,
    dashboardFiltered,
    chartHeight,
    onRetry,
  }: {
    shown: BiCardShown;
    /** The dashboard has a date filter, which a preview run skips. */
    dashboardFiltered: boolean;
    palette: BiPalette;
    chartHeight: number;
    onRetry: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { viz, data } = shown;
    const Icon = biVizIcon(viz.type);
    const centered = viz.style?.title?.align === BiTitleAlign.Center;
    const ready = data.kind === 'ready' ? data : null;
    return (
      <BiSurfaceCard style={{ padding: rpx(16), gap: rpx(12) }}>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(10) }}
        >
          <View
            style={{
              width: rpx(32),
              height: rpx(32),
              borderRadius: rpx(9),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tint(aiColors.amber, 0.14),
            }}
          >
            <Icon size={17} color={aiColors.amber} />
          </View>
          <View style={{ flex: 1, gap: rpx(2) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: centered ? 'center' : 'flex-start',
                gap: rpx(6),
              }}
            >
              {viz.style?.title?.show === false ? null : (
                <Typography.Text
                  medium
                  size={rpx(16)}
                  color={aiColors.foreground}
                >
                  {shown.title}
                </Typography.Text>
              )}
              {shown.unsaved ? (
                <BiTag
                  text={
                    shown.previewRun && dashboardFiltered
                      ? '预览未套用看板日期筛选，未保存'
                      : '预览，未保存'
                  }
                  color={aiColors.primary}
                />
              ) : null}
              {ready?.dateFilter === BiDateFilterUse.Ignored ? (
                <BiTag
                  text="未应用日期筛选"
                  color={aiColors.amber}
                  icon={CalendarX}
                />
              ) : null}
            </View>
            {ready ? (
              <Typography.Text
                size={rpx(12)}
                color={aiColors.mutedForeground}
                style={centered ? { textAlign: 'center' } : undefined}
              >
                {biCardSubtitle(viz, ready.result.current)}
              </Typography.Text>
            ) : null}
          </View>
        </View>
        <CardBody
          state={data}
          spec={shown.spec}
          viz={viz}
          kind={biCardKind(viz)}
          palette={palette}
          chartHeight={chartHeight}
          scroll={false}
          onRetry={onRetry}
        />
      </BiSurfaceCard>
    );
  },
);
